/**
 * Summarize how a node's parameters differ from its definition defaults.
 * Returns a string like "Width: 1024 · Height: 1024" for display in the save-node modal.
 *
 * Known gaps:
 *
 * 1. Utility-node options have no defaults to compare against.
 *    NodeDefinition.data from the server has no `options` field — WASM populates it
 *    at runtime based on connected inputs. So we can't tell "user changed this" from
 *    "WASM initialized this". Current fallback: show all non-trivial option values.
 *    To fix, provide default options via one of:
 *      a. Snapshot initial options at node creation time (node.data.initialOptions)
 *      b. Add a WASM API: getDefaultOptions(nodeId) → Record<string, unknown>
 *      c. Include default options in NodeDefinition.data from the server
 *
 * 2. Model-node definition matching picks the first custommodelV2 definition,
 *    not the specific model. Should match by data.kind.type instead of node.type.
 */
import isEqual from 'lodash/isEqual';
import type { BaseNodeData, Node } from '@/types/node';
import type { NodeDefinition } from '@/types/api/nodeDefinition';
import type { AiModelData } from '@/types/nodes/model';

/** Fields on BaseNodeData / internal state — not user-facing parameters. */
const INTERNAL_FIELDS = new Set([
  'color',
  'description',
  'handles',
  'initialData',
  'input',
  'inputNode',
  'isLocked',
  'iteratorInput',
  'model',
  'name',
  'output',
  'pastedData',
  'result',
  'type',
  'version',
  'viewMode',
]);

function isPrimitive(v: unknown): v is string | number | boolean {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}

function formatLabel(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
}

export function getNodeDefinitionId(node: Node): string {
  const data = node.data as Partial<AiModelData>;
  if (data.kind?.type && typeof data.kind.type === 'string') {
    return data.kind.type;
  }
  return node.type ?? '';
}

export function getChangedParamsDescription(node: Node, nodeTypes: NodeDefinition[]): string {
  const data = node.data as BaseNodeData & { params?: Record<string, any>; schema?: Record<string, any> };
  const def = nodeTypes.find((d) => d.type === node.type);

  // Model nodes: compare params against definition defaults (isEqual — params can be objects)
  if (data.params && data.schema) {
    const defaultParams = (def?.data as typeof data)?.params;
    if (!defaultParams) {
      return '';
    }
    return Object.entries(data.params)
      .filter(([key, value]) => defaultParams[key] !== undefined && !isEqual(value, defaultParams[key]))
      .map(([key, value]) => `${data.schema![key]?.title || key}: ${value}`)
      .join(' · ');
  }

  // Utility nodes: collect changed options + top-level primitive fields
  const currentData = node.data as unknown as Record<string, unknown>;
  const defaultData = def ? (def.data as unknown as Record<string, unknown>) : undefined;
  const options = currentData.options as Record<string, unknown> | undefined;
  const defaultOptions = defaultData?.options as Record<string, unknown> | undefined;
  const changed: string[] = [];

  if (options) {
    for (const [key, value] of Object.entries(options)) {
      if (INTERNAL_FIELDS.has(key) || !isPrimitive(value)) {
        continue;
      }
      if (defaultOptions) {
        if (defaultOptions[key] !== undefined && value !== defaultOptions[key]) {
          changed.push(`${formatLabel(key)}: ${value}`);
        }
      } else if (value !== 0 && value !== false && value !== '') {
        changed.push(`${formatLabel(key)}: ${value}`);
      }
    }
  }

  for (const [key, value] of Object.entries(currentData)) {
    if (INTERNAL_FIELDS.has(key) || !isPrimitive(value)) {
      continue;
    }
    if (defaultData?.[key] === undefined || value !== defaultData[key]) {
      changed.push(`${formatLabel(key)}: ${value}`);
    }
  }

  return changed.join(' · ');
}
